import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classnames from 'classnames';

import { configPlayer, mapAssetType } from './video-utils';
import { DEFAULT_CTA } from './metadata';
import closeIcon from './media/close.svg';
import passportIcon from './media/new_passport.svg';
import styles from './video-player.scss';
import Select from 'react-select';
import { getSeasons } from './ajax';
import { objectExistsInArray } from 'utils';
import { ALL_SEASONS } from './constants';
import { arraysEqual } from '../../utils';

class VideoPlayer extends Component {
    static propTypes = {
        API: PropTypes.object,
        componentId: PropTypes.string.isRequired,
        handleCTAChange: PropTypes.func.isRequired,
        CallToAction: PropTypes.func.isRequired,
        showVideo: PropTypes.bool.isRequired,
        playerClass: PropTypes.string,
        videoClose: PropTypes.func.isRequired,
        showCta: PropTypes.shape({
            link: PropTypes.object,
            url: PropTypes.string,
            text: PropTypes.string,
            textPlaceholder: PropTypes.string
        }),
        cta: PropTypes.shape({
            link: PropTypes.object,
            url: PropTypes.string,
            text: PropTypes.string,
            textPlaceholder: PropTypes.string
        }),
        settings: PropTypes.shape({
            displayCTA: PropTypes.bool.isRequired,
            displayShowCTA: PropTypes.bool.isRequired,
            selectedShows: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    title: PropTypes.string.isRequired,
                    links: PropTypes.array
                })
            ),
            selectedSeasons: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.string.isRequired
                })
            ),
            player: PropTypes.shape({
                topbar: PropTypes.bool.isRequired,
                autoplay: PropTypes.bool.isRequired
            }).isRequired,
            displayVideoTitle: PropTypes.bool.isRequired,
            displayShowTitle: PropTypes.bool.isRequired,
            displayVideoMeta: PropTypes.bool.isRequired,
            seasonFilter: PropTypes.bool
        }),
        callsign: PropTypes.string,
        currentSite: PropTypes.object,
        currentVideo: PropTypes.object,
        isSomethingDragging: PropTypes.bool.isRequired,
        reference: PropTypes.object,
        isPillButtonAvailable: PropTypes.bool,
        handleSelectSeasonFilter: PropTypes.func,
        site: PropTypes.object,
        selectedSeasonFilter: PropTypes.object,
        showId: PropTypes.string
    };

    static defaultProps = {
        cta: DEFAULT_CTA,
        playerClass: '',
        isSomethingDragging: false
    };

    componentDidMount = () => {
        const {
            currentSite,
            site,
            settings: { selectedShows, seasonFilter }
        } = this.props;

        const currentSiteId = currentSite?.siteId ? currentSite?.siteId : site?.id;

        if (seasonFilter) {
            this.getShowSeasons(currentSiteId, selectedShows, this.showLoading);
        }
    };

    componentDidUpdate(prevProps) {
        const {
            currentSite,
            site,
            settings: { selectedShows, seasonFilter, selectedSeasons }
        } = this.props;

        let prevSelectedSeasons = prevProps.settings.selectedSeasons
            ? prevProps.settings.selectedSeasons
            : [];
        const currentSiteId = currentSite?.siteId ? currentSite?.siteId : site?.id;
        if (
            (seasonFilter && seasonFilter !== prevProps.settings.seasonFilter) ||
            (selectedSeasons && !arraysEqual(prevSelectedSeasons, selectedSeasons))
        ) {
            this.getShowSeasons(currentSiteId, selectedShows, this.showLoading);
        }
    }

    getCallsign = () => {
        const { currentSite: { siteType, station } = {}, callsign } = this.props;
        if (callsign) {
            return callsign;
        }
        if (siteType === 'station' && station && station.callSign) {
            return station.callSign;
        }
        return '';
    };

    getShowSeasons(siteId, selectedShows, showLoading) {
        const {
            settings: { selectedSeasons },
            API
        } = this.props;
        let seasons = [];
        let selectedSeasonFilter = this.props.selectedSeasonFilter;

        if (
            (selectedSeasons &&
                selectedSeasons.length > 0 &&
                selectedSeasons[0].value !== ALL_SEASONS.value) ||
            selectedShows.length > 1
        ) {
            seasons = [ALL_SEASONS, ...selectedSeasons];
            if (selectedSeasonFilter && !objectExistsInArray(selectedSeasonFilter, seasons)) {
                selectedSeasonFilter = ALL_SEASONS;
            }
            this.setState({
                seasons: seasons,
                selectedSeasonFilter: selectedSeasonFilter
            });
        } else {
            getSeasons(API, siteId, selectedShows[0].id, showLoading).done((response) => {
                seasons = [
                    ALL_SEASONS,
                    ...response.map((item) => ({
                        label: item.ordinal.toString(), // Convert ordinal to string for label
                        value: item.id
                    }))
                ];
                this.setState({
                    seasons: seasons,
                    selectedSeasonFilter: selectedSeasonFilter
                });
            });
        }
    }

    render() {
        const {
            settings: {
                player,
                displayCTA,
                displayShowCTA,
                displayVideoTitle,
                displayShowTitle,
                displayVideoMeta,
                seasonFilter
            },
            currentVideo,
            componentId,
            handleCTAChange,
            cta,
            showCta,
            CallToAction,
            showVideo,
            playerClass,
            videoClose,
            isSomethingDragging,
            reference,
            isPillButtonAvailable,
            handleSelectSeasonFilter,
            selectedSeasonFilter,
            showId
        } = this.props;

        if (playerClass && !showVideo) {
            return null;
        }
        if (!currentVideo) {
            return null;
        }

        let seasons = this.state?.seasons;

        const {
            showTitle,
            videoTitle,
            duration,
            date,
            rating,
            type,
            longDescription,
            id,
            isPassportVideo,
            seasonInfo
        } = currentVideo;
        const videoMeta = [mapAssetType(type), seasonInfo, duration, date, rating]
            .filter((item) => {
                // First, check if the item exists (is not null/undefined)
                if (item) {
                    // Convert to string (important for numbers, booleans etc. if they might be in the array)
                    // Then trim whitespace from both ends and check if it's not an empty string
                    return String(item).trim() !== '';
                }
                return false; // Filter out null/undefined items
            })
            .join(' | ');
        const callsign = this.getCallsign();
        const config = callsign ? { ...player, callsign } : player;

        return (
            <div
                ref={reference}
                className={classnames({
                    [styles.videoPlayer]: true,
                    'video-player': true,
                    [playerClass]: true
                })}>
                {playerClass && <img className="close" src={closeIcon} onClick={videoClose} />}
                <div className="left-side">
                    <div
                        className={classnames({
                            'responsive-player-wrapper': true,
                            'top-bar': player.topbar,
                            'shape-placeholder-fill': true
                        })}>
                        {currentVideo.playerIframe && !isSomethingDragging && (
                            <div
                                className="player"
                                dangerouslySetInnerHTML={{
                                    __html: configPlayer(currentVideo.playerIframe, config)
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="right-side">
                    <div className="inner-left">
                        {displayShowTitle && (
                            <h4 className="text-placeholder show-title">{showTitle}</h4>
                        )}
                        {displayVideoTitle && (
                            <h3 className="text-placeholder video-title">{videoTitle}</h3>
                        )}
                        {displayVideoMeta && (
                            <div className="text-placeholder video-meta">
                                {isPassportVideo && (
                                    <img
                                        className="passport-icon"
                                        src={passportIcon}
                                        alt="Passport icon"
                                    />
                                )}
                                {videoMeta}
                            </div>
                        )}
                        <p className="text-placeholder-long double description">
                            {longDescription}
                        </p>
                    </div>
                    <div className="inner-right">
                        {displayCTA && (
                            <CallToAction
                                {...this.props}
                                componentId={componentId}
                                handleCTAChange={handleCTAChange}
                                videoId={id}
                                cta={cta}
                                isShowCTAAvailable={false}
                                isPillButtonAvailable={isPillButtonAvailable}
                            />
                        )}
                        {displayShowCTA && (
                            <CallToAction
                                {...this.props}
                                componentId={componentId}
                                handleCTAChange={handleCTAChange}
                                showId={showId}
                                cta={showCta}
                                isShowCTAAvailable={true}
                                isPillButtonAvailable={isPillButtonAvailable}
                            />
                        )}
                        {seasonFilter && (
                            <Select
                                aria-label="Season Filter"
                                options={seasons}
                                isClearable={false}
                                name="seasonFilter"
                                placeholder="Select season"
                                value={selectedSeasonFilter}
                                isSearchable={false}
                                onChange={(value) => handleSelectSeasonFilter(value)}
                                classNamePrefix="react-select"
                                className="season-filter"
                                onFocus={() => {}}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default VideoPlayer;
